package ui.components

import androidx.compose.runtime.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import domain.model.OrganizationAccessDiagnosis

@Composable
fun OrganizationAccessHelper(
    diagnosis: OrganizationAccessDiagnosis?,
    isLoading: Boolean = false,
    onRunDiagnosis: () -> Unit = {},
    onReauthorize: () -> Unit = {},
    onClose: () -> Unit = {}
) {
    // Modal overlay
    Div({
        style {
            position(Position.Fixed)
            top(0.px)
            left(0.px)
            width(100.percent)
            height(100.percent)
            backgroundColor(Color("rgba(0,0,0,0.5)"))
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            property("z-index", "9999")
        }
    }) {
        // Modal content
        Div({
            style {
                backgroundColor(Color.white)
                borderRadius(12.px)
                padding(24.px)
                maxWidth(600.px)
                maxHeight(80.vh)
                property("overflow-y", "auto")
                property("box-shadow", "0 20px 25px -5px rgba(0,0,0,0.1)")
                margin(20.px)
            }
        }) {
            // Header
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    alignItems(AlignItems.Center)
                    marginBottom(20.px)
                    paddingBottom(16.px)
                    property("border-bottom", "1px solid #e5e7eb")
                }
            }) {
                H3({
                    style {
                        margin(0.px)
                        fontSize(18.px)
                        fontWeight("600")
                        color(Color("#111827"))
                    }
                }) {
                    Text("🔍 Organization Access Diagnosis")
                }
                
                Button({
                    onClick { onClose() }
                    style {
                        backgroundColor(Color.transparent)
                        border(0.px)
                        fontSize(20.px)
                        cursor("pointer")
                        padding(4.px)
                        borderRadius(4.px)
                    }
                }) {
                    Text("✕")
                }
            }
            
            if (isLoading) {
                Div({
                    style {
                        textAlign("center")
                        padding(40.px)
                        color(Color("#6b7280"))
                    }
                }) {
                    Text("🔄 Running diagnosis...")
                }
            } else if (diagnosis != null) {
                // Diagnosis results
                DiagnosisResults(diagnosis, onReauthorize)
            } else {
                // Initial state
                DiagnosisIntro(onRunDiagnosis)
            }
        }
    }
}

@Composable
private fun DiagnosisIntro(onRunDiagnosis: () -> Unit) {
    Div({
        style {
            textAlign("center")
            padding(20.px)
        }
    }) {
        P({
            style {
                fontSize(16.px)
                color(Color("#374151"))
                property("line-height", "1.6")
                marginBottom(20.px)
            }
        }) {
            Text("It looks like you're only seeing a few organizations. This is usually due to OAuth application restrictions.")
        }
        
        P({
            style {
                fontSize(14.px)
                color(Color("#6b7280"))
                marginBottom(24.px)
            }
        }) {
            Text("Let's run a quick diagnosis to identify the issue and provide solutions.")
        }
        
        Button({
            onClick { onRunDiagnosis() }
            style {
                backgroundColor(Color("#3b82f6"))
                color(Color.white)
                border(0.px)
                borderRadius(8.px)
                padding(12.px, 24.px)
                fontSize(14.px)
                fontWeight("500")
                cursor("pointer")
                property("transition", "all 0.2s ease")
            }
        }) {
            Text("🔍 Run Diagnosis")
        }
    }
}

@Composable
private fun DiagnosisResults(
    diagnosis: OrganizationAccessDiagnosis,
    onReauthorize: () -> Unit
) {
    // Summary
    Div({
        style {
            backgroundColor(Color("#f3f4f6"))
            borderRadius(8.px)
            padding(16.px)
            marginBottom(20.px)
        }
    }) {
        H4({
            style {
                margin(0.px, 0.px, 8.px, 0.px)
                fontSize(14.px)
                fontWeight("600")
                color(Color("#374151"))
            }
        }) {
            Text("📊 Diagnosis Summary")
        }
        
        P({
            style {
                margin(0.px)
                fontSize(13.px)
                color(Color("#6b7280"))
            }
        }) {
            Text("User: ${diagnosis.userLogin} • Visible organizations: ${diagnosis.visibleOrganizationCount}")
        }
        
        if (diagnosis.visibleOrganizations.isNotEmpty()) {
            Div({
                style {
                    marginTop(8.px)
                }
            }) {
                diagnosis.visibleOrganizations.forEach { org ->
                    Span({
                        style {
                            display(DisplayStyle.InlineBlock)
                            backgroundColor(Color("#ddd6fe"))
                            color(Color("#5b21b6"))
                            fontSize(11.px)
                            padding(2.px, 6.px)
                            borderRadius(4.px)
                            marginRight(4.px)
                            marginTop(4.px)
                        }
                    }) {
                        Text(org)
                    }
                }
            }
        }
    }
    
    // Recommendations
    Div({
        style {
            marginBottom(20.px)
        }
    }) {
        H4({
            style {
                margin(0.px, 0.px, 12.px, 0.px)
                fontSize(14.px)
                fontWeight("600")
                color(Color("#374151"))
            }
        }) {
            Text("💡 Recommendations")
        }
        
        diagnosis.recommendations.forEach { recommendation ->
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.FlexStart)
                    marginBottom(8.px)
                    padding(8.px)
                    backgroundColor(Color("#fef9e7"))
                    property("border-left", "3px solid #f59e0b")
                    borderRadius(4.px)
                }
            }) {
                P({
                    style {
                        margin(0.px)
                        fontSize(13.px)
                        color(Color("#92400e"))
                        property("line-height", "1.5")
                    }
                }) {
                    Text(recommendation)
                }
            }
        }
    }
    
    // Action buttons
    Div({
        style {
            display(DisplayStyle.Flex)
            property("gap", "8px")
            justifyContent(JustifyContent.Center)
            flexWrap(FlexWrap.Wrap)
        }
    }) {
        Button({
            onClick { onReauthorize() }
            style {
                backgroundColor(Color("#10b981"))
                color(Color.white)
                border(0.px)
                borderRadius(6.px)
                padding(10.px, 16.px)
                fontSize(13.px)
                fontWeight("500")
                cursor("pointer")
                property("transition", "all 0.2s ease")
            }
        }) {
            Text("🔄 Re-authorize App")
        }
        
        Button({
            onClick {
                kotlinx.browser.window.open("https://github.com/settings/applications", "_blank")
            }
            style {
                backgroundColor(Color("#6366f1"))
                color(Color.white)
                border(0.px)
                borderRadius(6.px)
                padding(10.px, 16.px)
                fontSize(13.px)
                fontWeight("500")
                cursor("pointer")
                property("transition", "all 0.2s ease")
            }
        }) {
            Text("⚙️ Check OAuth Apps")
        }
        
        Button({
            onClick {
                kotlinx.browser.window.open("https://docs.github.com/en/organizations/managing-oauth-access-to-your-organizations-data/about-oauth-app-access-restrictions", "_blank")
            }
            style {
                backgroundColor(Color("#8b5cf6"))
                color(Color.white)
                border(0.px)
                borderRadius(6.px)
                padding(10.px, 16.px)
                fontSize(13.px)
                fontWeight("500")
                cursor("pointer")
                property("transition", "all 0.2s ease")
            }
        }) {
            Text("📚 Learn More")
        }
    }
}